package com.swetdot.swetgram.ui.groups

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import com.swetdot.swetgram.data.moderation.ReportTarget
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.ui.moderation.ReportRequest
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetDestructiveTextButton
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetSkeleton
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.profile.ProfileConfirmDialog
import com.swetdot.swetgram.ui.profile.ProfileNavRow
import com.swetdot.swetgram.ui.profile.ProfileSectionHeader
import com.swetdot.swetgram.ui.profile.QrDialog
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.text.resolveOrNull
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.discover_groups_delete_confirm_body
import swetgram.shared.generated.resources.discover_groups_delete_confirm_title
import swetgram.shared.generated.resources.discover_groups_delete_group
import swetgram.shared.generated.resources.discover_groups_discover_title
import swetgram.shared.generated.resources.discover_groups_group_title
import swetgram.shared.generated.resources.discover_groups_group_unavailable_body
import swetgram.shared.generated.resources.discover_groups_group_unavailable_title
import swetgram.shared.generated.resources.discover_groups_leave_confirm_body
import swetgram.shared.generated.resources.discover_groups_leave_confirm_title
import swetgram.shared.generated.resources.discover_groups_leave_group
import swetgram.shared.generated.resources.discover_groups_members
import swetgram.shared.generated.resources.discover_groups_report_group
import swetgram.shared.generated.resources.discover_groups_row_banned
import swetgram.shared.generated.resources.discover_groups_row_chat
import swetgram.shared.generated.resources.discover_groups_row_invite_link
import swetgram.shared.generated.resources.discover_groups_row_join_requests
import swetgram.shared.generated.resources.discover_groups_row_qr
import swetgram.shared.generated.resources.discover_groups_row_settings
import swetgram.shared.generated.resources.discover_groups_section_managing
import swetgram.shared.generated.resources.discover_groups_join_public
import swetgram.shared.generated.resources.discover_groups_try_again

/**
 * One group -- SPS §12 and SDS §14.
 *
 *
 * WHY THERE IS NO TAB BAR IN HERE
 *
 * SPS §12.1 and SDS §14.1 both model a group as three sections -- Chat, Discover, Members -- and
 * SDS §14.1's "Group Screen Navigation" lists them in that order. ALL THREE ARE NOW PRESENT; the
 * paragraph this replaces was written when Discover had no endpoints behind it and said what would
 * happen on the day it did: "when group Discover lands it becomes one more row in the same list,
 * between Chat and Members, and nothing else on this screen changes." That is exactly what
 * happened, and the reasons below are unchanged.
 *
 * The decision is that this screen is a group PROFILE with a list of entries, not a tabbed
 * container. Neither document says how the three sections are to be reached -- SDS §14.1 lists them
 * and numbers them and describes no control -- so this is SILENT in the folder and the following is
 * a DECISION. Three reasons, in the order they matter:
 *
 *  1. Chat is not a tab and cannot be one. Pass 1 established that a group's chat IS a conversation
 *     row of type `group`, so opening it must land on the SAME `ChatScreen` a direct message uses --
 *     a pushed, full-screen route with its own app bar, composer, keyboard insets and typing state.
 *     A "tab" that pushes a different screen is not a tab; it is a link wearing a tab's clothes, and
 *     the back gesture immediately gives it away.
 *  2. A two-tab bar would quietly restate the group model. §12.1's three sections are the product's
 *     "strongest differentiator" in its own words; shipping two of them inside a control whose whole
 *     job is to enumerate a complete set says the set is two.
 *  3. The design system has no secondary tab component. SDS §11.6's tabs are the four bottom-nav
 *     tabs and nothing else, so a tab strip here would be an invented component. That reason was
 *     stated with "and inventing one to hold a single real destination is the wrong order of
 *     operations", which is the half that Discover's arrival could have weakened -- there are two
 *     pushable destinations now, not one. It does not weaken the reason: point 1 is what rules a
 *     tab bar out, and it applies to a three-tab strip exactly as it did to a two-tab one. A
 *     control whose first tab pushes a full-screen conversation with its own bar, composer and
 *     back gesture is not a tab bar however many tabs it has.
 *
 *
 * WHAT THE LIST HOLDS NOW
 *
 * Chat, Discover, Members -- SDS §14.1's order, complete -- and then up to three more rows that
 * only some viewers see: the invite link, a private group's join queue, and the ban list. They are
 * rows in the same list rather than a separate "manage" screen, for the reason the section above
 * gives -- this screen is a group profile with entries, and a moderator's entries are simply more
 * of them.
 *
 * They are ABSENT for anyone who may not use them, never disabled. SDS §25.1's disabled state is
 * for something momentarily unavailable; "you are not an admin of this group" is not momentary, and
 * a greyed row invites a tap that can only ever refuse.
 *
 * WHAT IS STILL ABSENT: `transferGroupOwnership`, which the repository can call and no screen
 * offers, so the owner-cannot-leave sentence below remains true. And the group's settings
 * (`chat_posting`, `discover_posting`), which get their own screen -- a policy shown without the
 * control that changes it is a question with no answer.
 */
@Composable
fun GroupScreen(
    state: GroupUiState,
    onBack: () -> Unit,
    /**
     * Opens the group's CHAT -- the same [com.swetdot.swetgram.ui.chat.ChatScreen] a direct message
     * opens, addressed by the group's `conversation_id`.
     *
     * It carries the id because the caller does not have it: a group's id and its conversation's id
     * are two different values (`protocol/dto/Groups.kt` is explicit that nothing says they match),
     * and the conversation id arrives only with the loaded group. The navigation host holds the
     * GROUP id from its own route argument, which is why [onOpenMembers] takes nothing.
     */
    onOpenChat: (ChatId) -> Unit,
    /**
     * SPS §12.5's group Discover area, pushed as its own route.
     *
     * Takes nothing, like [onOpenMembers]: the host already holds the GROUP id from its own route
     * argument, and this feed is addressed by that id -- `GET /groups/{group_id}/discover/posts`.
     * Unlike [onOpenChat], there is no second id to carry.
     *
     * ALWAYS DRAWN, WHICH BREAKS THIS SCREEN'S OWN "ABSENT, NEVER DISABLED" RULE IN ONE DIRECTION
     * AND KEEPS IT IN THE OTHER -- worth being exact about, because the three moderation rows below
     * are hidden and this one is not.
     *
     * Those rows are hidden because the client can ANSWER their question: `Group.permissions` comes
     * from SPS §12.7's table applied to a role the server sent. Reading this feed asks a different
     * question -- am I a member? -- and the client cannot answer it. `Group.role` is required by
     * the frozen contract and its enum has no member for "not in this group", so the server reports
     * a public group's NON-MEMBER as `member` and says so in its own projection. There is no
     * membership flag anywhere in v1.0.0.
     *
     * So the choice is between hiding the row from real members on a guess, and showing it to a
     * stranger who then reads "Only members of this group can read its Discover feed." on the
     * screen it opens. The second is the smaller failure, and it is the only one that is honest:
     * SPS §12.1 makes Discover one of a group's three sections, and a group profile that silently
     * omits a third of the model on a guess is describing a different product.
     */
    onOpenDiscover: (() -> Unit)? = null,
    /** SPS §12.6's Members tab, pushed as its own route. */
    onOpenMembers: () -> Unit,
    /**
     * SPS §12.3's invite link, pushed as its own route.
     *
     * Its own screen rather than a dialog from here, and that is not a layout preference: the
     * screen holds a credential, and the only way to see one is a call that may INVALIDATE the
     * link the group already shared. That needs somewhere with room to explain itself before
     * anything is pressed. Takes nothing -- the host already holds the group id.
     */
    onOpenInviteLink: () -> Unit,
    /** A private group's approval queue. Owner and Admin. */
    onOpenJoinRequests: () -> Unit,
    /** The ban list -- the only place a ban can be lifted. Owner and Admin. */
    onOpenBans: () -> Unit,
    onOpenSettings: () -> Unit,
    onShowQr: () -> Unit,
    onDismissQr: () -> Unit,
    onJoin: () -> Unit,
    onLeave: () -> Unit,
    onDelete: () -> Unit,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
    /**
     * SPS §16.3's report, for a `group` target.
     *
     * A DECISION as to placement: SPS §16.3 lists groups among the five reportable things and the
     * contract's `CreateReportRequest.target_type` carries `group`, but SDS names no control for it
     * anywhere -- §14 gives the group screen an identity block and a section list and no report
     * action. This screen is the only place a group is the subject, so this is where it goes.
     *
     * Null when reporting is not wired on this build; the control is then absent, not disabled.
     */
    onReport: ((ReportRequest) -> Unit)? = null,
) {
    val colors = SwetgramThemeTokens.colors
    var confirming by remember { mutableStateOf<GroupConfirmation?>(null) }
    val group = state.group

    Column(modifier.fillMaxSize()) {
        //
        // The bar says "Group" rather than the group's name, exactly as `PublicProfileScreen` says
        // "Profile": the name is the largest thing in the identity block a few dp below, and
        // printing it twice makes the screen read as though it is about two different things.
        SwetTopAppBar(title = stringResource(Res.string.discover_groups_group_title), onBack = onBack)

        // Over the content, never in place of it -- a failed refresh must not take away the Leave
        // button, which may be the reason the user came here.
        if (state.error != null) {
            SwetErrorBanner(
                message = state.error.resolve(),
                onRetry = onRetry,
                onDismiss = onDismissError,
            )
        }

        when {
            group == null && !state.loadedOnce -> GroupSkeleton()

            group == null -> SwetEmptyState(
                title = stringResource(Res.string.discover_groups_group_unavailable_title),
                body = stringResource(Res.string.discover_groups_group_unavailable_body),
                actionText = onRetry?.let { stringResource(Res.string.discover_groups_try_again) },
                onAction = onRetry,
            )

            else -> Column(
                modifier = Modifier
                    .fillMaxSize()
                    // A pushed detail route, so no tab bar underneath carries the system-bar
                    // inset. Applied before `verticalScroll` so the inset is padding around the
                    // scroller rather than content inside it.
                    .navigationBarsPadding()
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Spacer(Modifier.height(Space.four))

                GroupIdentity(
                    name = group.name,
                    avatarUrl = group.avatarUrl,
                    subtitle = groupSubtitle(
                        visibility = group.visibility,
                        // Non-null only when the server sent a complete public address; a private
                        // group has none, and `domain/Group.kt` refuses to invent half of one.
                        username = group.publicAddress?.username,
                        memberCount = group.memberCount,
                    ).resolve(),
                    description = group.description,
                )

                Spacer(Modifier.height(Space.four))

                if (!group.isMember) {
                    SwetPrimaryButton(
                        text = stringResource(Res.string.discover_groups_join_public),
                        onClick = onJoin,
                        enabled = !state.busy,
                        modifier = Modifier.padding(horizontal = Space.two),
                    )
                    Spacer(Modifier.height(Space.three))
                }

                if (group.isMember) {

                // SDS §14.1's section order, complete: Chat, Discover, Members.
                //
                // No section heading above them: the app bar already says "Group", and a
                // `ProfileSectionHeader` reading the same word twice on one screen is the kind of
                // label that exists only to fill a slot. The 32dp above does the separating.
                ProfileNavRow(
                    title = stringResource(Res.string.discover_groups_row_chat),
                    onClick = { onOpenChat(group.conversationId) },
                )
                if (onOpenDiscover != null) ProfileNavRow(
                    title = stringResource(Res.string.discover_groups_discover_title),
                    // NO VALUE ON THIS ROW, for the duller of the two reasons the rows below give.
                    // The Members row can carry a count because `Group.member_count` is already in
                    // the response this screen was drawn from. Nothing in `Group` counts posts, and
                    // v1.0.0 declares no operation that does -- the only way to a number is to read
                    // the first page of the feed, which would cost a request on every group-screen
                    // open and would still be stale by the time it was drawn. The same argument the
                    // Join requests row makes about its own queue.
                    onClick = onOpenDiscover,
                )
                ProfileNavRow(
                    title = stringResource(Res.string.discover_groups_members),
                    // The count, so the row answers the most common question about a group
                    // without being tapped.
                    value = memberCountLine(group.memberCount).resolve(),
                    onClick = onOpenMembers,
                )

                if (state.canOpenSettings) {
                    ProfileNavRow(
                        title = stringResource(Res.string.discover_groups_row_settings),
                        onClick = onOpenSettings,
                    )
                }

                if (state.canShowQr) {
                    ProfileNavRow(
                        title = stringResource(Res.string.discover_groups_row_qr),
                        onClick = onShowQr,
                    )
                }

                // The moderation entries, each present only for someone who can use it. A heading
                // is drawn only when at least one of them is -- an empty section header on a
                // member's screen would announce a set of controls they will never see.
                if (state.canOpenInviteLink || state.canOpenJoinRequests || state.canOpenBans) {
                    Spacer(Modifier.height(Space.three))
                    ProfileSectionHeader(text = stringResource(Res.string.discover_groups_section_managing))
                }

                if (state.canOpenInviteLink) {
                    ProfileNavRow(
                        title = stringResource(Res.string.discover_groups_row_invite_link),
                        // NO VALUE ON THIS ROW, and its absence is the whole design. A group's link
                        // can only be read by an operation that also rotates it, so showing the
                        // current link here would mean minting a new one every time this screen
                        // loaded -- quietly breaking the link the group had already shared, on a
                        // screen the user only opened to look at something else.
                        onClick = onOpenInviteLink,
                    )
                }

                if (state.canOpenJoinRequests) {
                    ProfileNavRow(
                        title = stringResource(Res.string.discover_groups_row_join_requests),
                        // No count either, and for a duller reason than the row above: nothing in
                        // `Group` carries one, and the only way to get it is to read the first page
                        // of the queue. A number on this row would cost a request per group screen
                        // open and would still be stale by the time it was drawn.
                        onClick = onOpenJoinRequests,
                    )
                }

                if (state.canOpenBans) {
                    ProfileNavRow(
                        title = stringResource(Res.string.discover_groups_row_banned),
                        onClick = onOpenBans,
                    )
                }

                Spacer(Modifier.height(Space.three))

                // The way out of this group, decided once. See `groupExitOption` for why an owner
                // gets a sentence where everybody else gets a button.
                val exit = groupExitOption(group)

                groupExitLine(exit).resolveOrNull()?.let { line ->
                    Text(
                        text = line,
                        style = MaterialTheme.typography.bodySmall,
                        color = colors.textSecondary,
                        textAlign = TextAlign.Start,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = Space.two),
                    )
                    Spacer(Modifier.height(Space.two))
                }

                if (exit == GroupExitOption.LEAVE) {
                    SwetDestructiveTextButton(
                        text = stringResource(Res.string.discover_groups_leave_group),
                        onClick = { confirming = GroupConfirmation.Leave },
                        enabled = !state.busy,
                    )
                }

                if (state.canDelete) {
                    SwetDestructiveTextButton(
                        text = stringResource(Res.string.discover_groups_delete_group),
                        onClick = { confirming = GroupConfirmation.Delete },
                        enabled = !state.busy,
                    )
                }
                }

                // NOT OFFERED TO THE OWNER, which `canDelete` is the client's name for: somebody
                // who can delete a group outright has no use for asking a moderator to look at it,
                // and the row would only ever be a mis-tap. Everybody else -- members, admins and
                // people looking at a public group they have not joined -- gets it.
                //
                // `danger`, like Leave and Delete above it. This used to be the accent-blue
                // `SwetTextButton` on the argument that Leave and Delete destroy something while
                // this one only asks somebody to look, and that three red buttons in a column would
                // stop the destructive two reading as warnings. Both halves were wrong:
                //
                //  * SDS §11.1 names "Report user" as a DESTRUCTIVE BUTTON usage in the same list
                //    as "Leave group", and §4.6 assigns `danger` to "Delete, report, destructive
                //    actions" by name. Report is not a borderline case in the spec; it is an
                //    enumerated one.
                //  * There were never three. This is drawn only when `!state.canDelete`, so Delete
                //    is absent whenever Report is present -- the most this column can hold is Leave
                //    and Report.
                //
                // Same stacked full-width component as the two above it rather than
                // `SwetTextButton(destructive = true)`: that parameter exists for INLINE rows like
                // `CommentRow`'s, and mixing widths in one stack would be the drift, not the fix.
                if (onReport != null && !state.canDelete) {
                    SwetDestructiveTextButton(
                        text = stringResource(Res.string.discover_groups_report_group),
                        onClick = {
                            onReport(
                                ReportRequest(
                                    target = ReportTarget.Group(group.id),
                                    // No account: a group is not a person, so the receipt makes no
                                    // block offer. There is nobody here to block.
                                    groupName = group.name,
                                ),
                            )
                        },
                        enabled = !state.busy,
                    )
                }

                Spacer(Modifier.height(Space.six))
            }
        }
    }

    // SDS §11.1: "Use confirmation dialogs for destructive actions." Both of these are permanent
    // for at least one person, so both are confirmed, and each body names the consequence the user
    // could not otherwise know rather than asking "are you sure?".
    when (confirming) {
        null -> Unit

        GroupConfirmation.Leave -> ProfileConfirmDialog(
            title = stringResource(
                Res.string.discover_groups_leave_confirm_title,
                group?.name.orEmpty(),
            ),
            // The second sentence is DECISIONS.md §7's history rule, and it is the part nobody
            // expects: `min_visible_seq` is set to the conversation's current position on every
            // add, so a member who leaves and is added back starts from empty.
            body = stringResource(Res.string.discover_groups_leave_confirm_body),
            confirmText = stringResource(Res.string.discover_groups_leave_group),
            onConfirm = {
                confirming = null
                onLeave()
            },
            onDismiss = { confirming = null },
            busy = state.busy,
        )

        GroupConfirmation.Delete -> ProfileConfirmDialog(
            title = stringResource(
                Res.string.discover_groups_delete_confirm_title,
                group?.name.orEmpty(),
            ),
            // What `deleteGroup` actually does: it deletes the CONVERSATION and lets the cascade
            // take the members, the messages and the pins with it. Everyone loses it, not just
            // the owner, and that is the fact the owner is being asked to agree to.
            body = stringResource(Res.string.discover_groups_delete_confirm_body),
            confirmText = stringResource(Res.string.discover_groups_delete_group),
            onConfirm = {
                confirming = null
                onDelete()
            },
            onDismiss = { confirming = null },
            busy = state.busy,
        )
    }

    if (state.qr.visible) {
        QrDialog(state = state.qr, onDismiss = onDismissQr)
    }
}

private enum class GroupConfirmation { Leave, Delete }

/**
 * A group's identity block: picture, name, what kind of group it is, and its description.
 *
 * NOT `ProfileIdentity`, and the difference is not cosmetic. That component is built around a
 * person: it renders `@username` unconditionally and takes a nullable `isOnline` whose three values
 * are a privacy distinction. A group has no presence at all, and only a PUBLIC group has a
 * username, so reusing it would mean passing an empty handle to be rendered as a bare "@" and a
 * presence argument that means nothing. Every measurement below is the one `ProfileIdentity` uses,
 * so a group and a person sit on the same grid.
 *
 * `SwetAvatar` is reused as-is. Its deterministic initials fallback is exactly SDS §14.1's
 * "generated initials" for a group with no picture, and it is already the component that draws a
 * group's photo in the chat list -- a second drawing would let the two drift.
 */
@Composable
private fun GroupIdentity(
    name: String,
    avatarUrl: String?,
    subtitle: String,
    description: String?,
) {
    val colors = SwetgramThemeTokens.colors

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = Space.screenMargin),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SwetAvatar(
            displayName = name,
            size = Sizes.avatarProfile,
            surfaceColor = colors.background,
            avatarUrl = avatarUrl,
            // A group is never online. The dot is not drawn, so its description would never be
            // read -- but passing null states that on purpose rather than by omission.
            isOnline = false,
            onlineContentDescription = null,
        )

        Spacer(Modifier.height(Space.two))

        Text(
            text = name,
            style = MaterialTheme.typography.headlineSmall,
            color = colors.textPrimary,
            textAlign = TextAlign.Center,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
        )

        Spacer(Modifier.height(Space.half))

        Text(
            text = subtitle,
            style = MaterialTheme.typography.bodyMedium,
            color = colors.textSecondary,
            textAlign = TextAlign.Center,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
        )

        if (!description.isNullOrBlank()) {
            Spacer(Modifier.height(Space.two))
            Text(
                text = description,
                style = MaterialTheme.typography.bodyMedium,
                color = colors.textPrimary,
                textAlign = TextAlign.Center,
            )
        }
    }
}

/**
 * What this screen looks like while the group is on its way.
 *
 * A skeleton rather than a spinner, for the reason `PublicProfileSkeleton` gives: this screen is
 * reached by tapping a group, a whole page of layout arrives at once, and a centred spinner
 * guarantees a reflow on every open rather than only on a slow connection. The placeholders are at
 * the sizes [GroupIdentity] and the rows below it actually use.
 */
@Composable
private fun GroupSkeleton() {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = Space.screenMargin),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Spacer(Modifier.height(Space.four))

        SwetSkeleton(Modifier.size(Sizes.avatarProfile), shape = CircleShape)

        Spacer(Modifier.height(Space.two))
        // Name, then the "Private group · 12 members" line. Deliberately unequal widths, as
        // `SwetListRowSkeleton` is: two bars of the same width read as a rendering fault.
        SwetSkeleton(Modifier.fillMaxWidth(0.5f).height(Space.three))
        Spacer(Modifier.height(Space.one))
        SwetSkeleton(Modifier.fillMaxWidth(0.34f).height(Space.two))

        Spacer(Modifier.height(Space.four))
        // The three navigation rows every viewer gets -- Chat, Discover, Members -- at
        // `ProfileNavRow`'s own minimum height. The moderation rows below them are deliberately not
        // drawn here: whether they appear depends on a role this skeleton has not read yet, and
        // placeholders for controls that may never arrive would make the screen shrink on load.
        repeat(3) {
            SwetSkeleton(
                Modifier.fillMaxWidth().height(TouchTarget.recommended),
                shape = RoundedCornerShape(Radius.sm),
            )
            Spacer(Modifier.height(Space.one))
        }
    }
}
